import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Suite genelinde ortak "ücretsiz deneme / abonelik bitiyor" şeridi.
// Tek kaynak: isosofts-api → GET /api/company/self/trial-status (auth_token cookie).
// Sabit ALT şerit; uygulamaların fixed navbar'ına ve sayfa boşluklarına dokunmaz.
//
// NOT: Bu bileşenin JSX ikizi isosofts / algebra / architecture arayüzlerinde de var
// (components/TrialBanner.jsx). Birini değiştirirken diğerlerini de güncelle.

type Dict = { trial: string; subscription: string; ends: string; contact: string; dismiss: string; note: string };

const TEXT: Record<string, Dict> = {
  en: { trial: 'Free trial', subscription: 'Subscription', ends: 'Ends', contact: 'Contact us', dismiss: 'Dismiss', note: 'Your data is never deleted.' },
  az: { trial: 'Pulsuz sınaq', subscription: 'Abunəlik', ends: 'Bitmə tarixi', contact: 'Bizimlə əlaqə', dismiss: 'Bağla', note: 'Məlumatlarınız heç vaxt silinmir.' },
  ru: { trial: 'Бесплатный период', subscription: 'Подписка', ends: 'Дата окончания', contact: 'Связаться с нами', dismiss: 'Закрыть', note: 'Ваши данные никогда не удаляются.' },
  lt: { trial: 'Nemokamas bandymas', subscription: 'Prenumerata', ends: 'Pabaiga', contact: 'Susisiekite', dismiss: 'Uždaryti', note: 'Jūsų duomenys niekada neištrinami.' },
};

const daysPhrase = (lang: string, n: number): string => {
  if (lang === 'az') return n === 0 ? 'bu gün bitir' : n === 1 ? 'sabah bitir' : `${n} gün qaldı`;
  if (lang === 'ru') return n === 0 ? 'заканчивается сегодня' : n === 1 ? 'заканчивается завтра' : `осталось дней: ${n}`;
  if (lang === 'lt') return n === 0 ? 'baigiasi šiandien' : n === 1 ? 'baigiasi rytoj' : `liko ${n} d.`;
  return n === 0 ? 'ends today' : n === 1 ? 'ends tomorrow' : `${n} days left`;
};

const getToken = (): string =>
  document.cookie.split('; ').find((r) => r.startsWith('auth_token='))?.split('=').slice(1).join('=') ?? '';

type TrialStatus = { isTrial: number; deadline: string; daysLeft: number | null };

export function TrialBanner() {
  const { i18n } = useTranslation();
  const [status, setStatus] = useState<TrialStatus | null>(null);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (!token) return;

    fetch(`https://isosofts.com/api/company/self/trial-status?token=${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setStatus(d))
      .catch(() => {});
  }, []);

  if (!status || closed) return null;

  const left = status.daysLeft;
  const isTrial = status.isTrial === 1;

  // Süresiz abonelik → gösterme. Deneme boyunca hep, ücretlide son 7 günde göster.
  if (left === null || left === undefined || left < 0) return null;
  if (!isTrial && left > 7) return null;

  const lang = (i18n.language || 'en').slice(0, 2);
  const T = TEXT[lang] ?? TEXT.en;
  const urgent = left <= 3;
  const key = `isosofts_banner_dismissed_${status.deadline}`;

  try {
    if (sessionStorage.getItem(key) === '1') return null;
  } catch {
    /* sessionStorage kapalı olabilir */
  }

  const dismiss = () => {
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      /* yoksay */
    }
    setClosed(true);
  };

  return (
    <div
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 9999,
        display: 'flex', justifyContent: 'center', padding: '0 12px 12px', pointerEvents: 'none',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          maxWidth: 860, width: '100%', padding: '11px 16px', borderRadius: 12,
          background: urgent ? '#7f1d1d' : '#1e3a5f',
          border: `1px solid ${urgent ? '#b91c1c' : '#2c5282'}`,
          boxShadow: '0 12px 32px rgba(0,0,0,0.28)',
          color: '#fff', fontSize: 13.5, lineHeight: 1.5,
          fontFamily: 'Arial, Helvetica, sans-serif',
        }}
      >
        <span
          style={{
            padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            background: urgent ? '#fecaca' : '#bfdbfe',
            color: urgent ? '#7f1d1d' : '#1e3a5f', flexShrink: 0,
          }}
        >
          {isTrial ? T.trial : T.subscription}
        </span>

        <span style={{ fontWeight: 700 }}>{daysPhrase(lang, left)}</span>
        <span style={{ opacity: 0.75 }}>
          {T.ends}: {status.deadline}
        </span>
        <span style={{ opacity: 0.6, fontSize: 12.5 }}>{T.note}</span>

        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <a
            href="https://isosofts.com/#contact"
            style={{
              padding: '7px 14px', borderRadius: 9, background: '#fff',
              color: urgent ? '#7f1d1d' : '#1e3a5f', fontWeight: 700,
              textDecoration: 'none', fontSize: 12.5, whiteSpace: 'nowrap',
            }}
          >
            {T.contact}
          </a>
          <button
            onClick={dismiss}
            aria-label={T.dismiss}
            style={{
              background: 'transparent', border: 'none', color: '#fff',
              opacity: 0.7, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px',
            }}
          >
            ×
          </button>
        </span>
      </div>
    </div>
  );
}
